import { JSX, useContext, useEffect, useState } from "react";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { AffiliationsDataGroup } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import ContractEntityProfile from "@app/projects/servicing-provider/provider-affiliations/ContractEntityProfile";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import DashboardTile from "@app/shared/qes-standards/DashboardTile";
import { ContractDisplayInformation, BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { AffiliationsData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/AffiliationsData";

interface AffiliationsProps {
  affiliationsVersion: number;
  affiliationsVersionLoading: boolean;
  data: AffiliationsData[];
  getContractDisplayInformation: (contractId: string) => ContractDisplayInformation;
  loading: boolean;
  onContractAffiliationsClick: (selectedAffiliationsContract: BenchmarkingNetworkType) => void;
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
    isHospitalAndPcp?: boolean,
    professionalContractEntityId?: number,
  ) => void;
  selectedAffiliationsNetwork: BenchmarkingNetworkType;
  selectedLineOfBusiness: BenchmarkingLineOfBusinessEnum;
  selectedNetworks: BenchmarkingNetworkType[];
  widthClass: string;
}

interface AffiliationsWaffleToppings {
  rank: JSX.Element;
  contractEntityName: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const Affiliations = (props: AffiliationsProps): JSX.Element => {
  const [tableData, setTableData] = useState<AffiliationsWaffleToppings[]>([]);

  const filterContext = useContext(BenchmarkingFiltersContext);

  const marketContract: string = "Market Data";

  useEffect(() => {
    if (!!props.data && !!props.selectedNetworks?.length) {
      /* oxlint-disable sort-keys */
      const newTableData: AffiliationsWaffleToppings[] = props.data.map((s, index) => {
        return {
          [wafflesKeyColumnName]: s.contractEntityName || index.toString(),
          rank: (
            <span className="text-muted">{index + 1}</span>
          ),
          contractEntityName: (
            <span>
              <FlyoutLink
                disabled={!props.affiliationsVersion || props.affiliationsVersionLoading}
                content={{
                  body:
                    <ContractEntityProfile
                      contractEntityId={s.professionalContractEntityId ?? 0}
                      contractEntityName={s.contractEntityName}
                      providerEntityType={""}
                      versionId={props.affiliationsVersion}
                      showPerformanceOverview={true}
                    />,
                  title: "Contract Entity Profile",
                }
                }
                classNames="qes-button-secondary"
                testId={`benchmarking-dashboard--affiliations__contract-entity-flyout-link-${index}`}
              >
                {s.contractEntityName}
              </FlyoutLink>
            </span>
          ),
        }
      });
      /* oxlint-enable sort-keys */

      for (const x of newTableData) {
        const ad: AffiliationsData = props.data.find(x => x.professionalContractEntityId === x.professionalContractEntityId);
        ad.contracts.forEach((i) => {
          Object.assign(x, {
            [i.contractId]: getCountDisplay(x.key, i.contractId, i.versionId),
          });
        });
      }
      setTableData(newTableData);
    }
  }, [props.data, props.selectedNetworks]);

  const getCountDisplay = (contractEntityName: string, contractId: string, versionId: number): JSX.Element => {
    const contractEntity: AffiliationsData = props.data.find(x => x.contractEntityName === contractEntityName);
    const affiliations: AffiliationsDataGroup = contractEntity?.contracts?.find(x => x.contractId === contractId);
    const count = affiliations?.affiliationsCount;

    if (!!count) {
      return (
        <QESButton
          qesTestId="network-benchmarking-dashboard__affiliations--count"
          qesType="secondary"
          onClick={() => onDashboardDetailsViewClick(count, versionId, contractEntity.professionalContractEntityId)}
          className="affiliations-count"
        >
          {printNumberAsString(count, "---")}
        </QESButton>
      );
    } else {
      return (
        <span className="text-muted affiliations-count">
          0
        </span>
      );
    }
  }

  const getColumnDisplay = (contractId: string): JSX.Element => {
    if(contractId === marketContract) return (<span>Market</span>);

    switch (props.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const splitContract: string[] = contractId.split("_");

        return (
          <span><span className="text-muted">{splitContract[0]}</span><br />{splitContract[1]}</span>
        );
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (
          <span>{contractId}</span>
        );
      case BenchmarkingLineOfBusinessEnum.Medicaid:
        const contractDisplayInformation: ContractDisplayInformation = props.getContractDisplayInformation(contractId);

        return (
          <span className="affiliations-parent-org-name">{contractDisplayInformation.parentOrganizationName}</span>
        );
      default:
        return;
    }
  }

  const getAffiliationsTitleLabel = (): string => {
    const contractId = props.selectedAffiliationsNetwork?.contractId;
    if (!contractId) return "";

    if (props.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid) {
      const contractDisplayInformation: ContractDisplayInformation = props.getContractDisplayInformation(contractId);
      return !!contractDisplayInformation?.parentOrganizationName
        ? `${contractDisplayInformation.parentOrganizationName} - ${contractDisplayInformation.networkName}`
        : "";
    }

    return contractId.replace(/_/g, " ");
  }

  const getColumns = (): ColumnConfig => {
    const columnConfig: ColumnConfig = {
      contractEntityName: {
        alignment: TextAlignment.Left,
        displayText: "",
        noSort: true,
        testId: "category",
      },
      rank: {
        alignment: TextAlignment.Left,
        displayText: "",
        noSort: true,
        testId: "rank",
      },
    };

    const getContractTooltip = (contractId: string): JSX.Element => {
      const contractDisplayInformation = props.getContractDisplayInformation(contractId)

      if (!!contractDisplayInformation) {
        return (
          <span>
            {contractDisplayInformation.parentOrganizationName} <br />
            {contractDisplayInformation.networkName}
          </span>
        );
      }
    }

    const byContract = (a: AffiliationsDataGroup, b: AffiliationsDataGroup) => {
      const primaryContract: string = props.selectedNetworks[0].contractId;
      if (a.contractId === primaryContract) return -1;
      if (b.contractId === primaryContract) return 1;
      if (a.contractId === marketContract) return 1;
      if (b.contractId === marketContract) return -1;

      return a.contractId.localeCompare(b.contractId);
    }

    const contractColor = (contractId: string) => {
      if(props.selectedNetworks[0]?.contractId === contractId) return "green";
      if(contractId === marketContract) return "bg-market";
      return "blue";
    }

    const contracts: AffiliationsDataGroup[] = !!props.data?.length ? props.data[0]?.contracts : [];

    contracts.sort(byContract).forEach((i) => {
      columnConfig[i.contractId] = {
        alignment: TextAlignment.Left,
        displayElement: (
          <TooltipWrapper
            containerClassName="text-center"
            text={getContractTooltip(i.contractId)}
            triggerWidth="100%"
          >
            <QESButton
              qesTestId={`network-benchmarking-dashboard__affiliations--count-${i.contractId}`}
              qesType="secondary"
              onClick={() => onContractAffiliationsClick(i)}
              className={`affiliations-button ${contractColor(i.contractId)}`}
            >
              {getColumnDisplay(i.contractId)}
            </QESButton>
          </TooltipWrapper>
        ),
        isSelected: props.selectedAffiliationsNetwork?.contractId === i.contractId,
        noSort: true,
        testId: i.contractId,
      };
    })

    return columnConfig;
  }

  const hasAffiliations = (): boolean => {
    return filterContext.isAffiliationState();
  }

  const onContractAffiliationsClick = (network: AffiliationsDataGroup): void => {
    if (hasAffiliations()) {
      props.onContractAffiliationsClick(network as unknown as BenchmarkingNetworkType);
    }
  }

  const onDashboardDetailsViewClick = (count: number, versionId: number, professionalContractEntityId: number): void => {
    if (hasAffiliations()) {
      props.onDashboardDetailsViewClick(BenchmarkingDashboardDetailsTypeEnum.Affiliations, count, versionId, null, null, null, professionalContractEntityId);
    }
  }

  const rowDecorator = (row: AffiliationsWaffleToppings): string => {
    if (!!row) {
      return "footer";
    }
  }

  return (
    <DashboardTile
      loading={props.loading}
      title={`Largest Contract Entities for ${getAffiliationsTitleLabel()}`}
      subtitle={"Shows the largest contract entities for each network based on number of NPIs. Clicking on the contract ID/ network will sort the contract entities from largest to smallest for the contract ID/ network that is selected."}
      widthClass={props.widthClass}
    >
      <div className={`${!hasAffiliations() && "affiliations-blur-container"}`}>
        <div className={`affiliations-table-container ${!hasAffiliations() && "affiliations-blur"}`}>
          <Waffles<AffiliationsWaffleToppings>
            columns={getColumns()}
            data={tableData}
            disableStickyHeader={true}
            fullWidth={true}
            keyColumns={["contractEntity"]}
            rowDecorator={rowDecorator}
            testId="network-benchmarking-dashboard--affiliations__table"
            theme={WafflesThemes.Simple}
          />
        </div>

        {!hasAffiliations() && (
          <div className="affiliations-blur-text">
            Affiliation Insights License Required to View This Information
          </div>
        )}
      </div>
    </DashboardTile>
  )
}

export default Affiliations;